import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import {
  eleccionDe,
  TODAS_LAS_CUENTAS,
} from '#components/mobile/budget/cuentaCategoria';
import type { EleccionCuenta } from '#components/mobile/budget/cuentaCategoria';
import { useCuentasCategorias } from '#components/mobile/budget/useCuentasCategorias';
import { useCuentasElegidas } from '#components/mobile/budget/usePagosFijos';
import { HojaSeleccion } from '#components/mobile/ui/HojaSeleccion';
import { estiloTarjeta } from '#components/mobile/ui/Tarjeta';
import { color, densidad, TACTIL } from '#components/mobile/ui/tokens';

const AUTOMATICA = '__automatica__';

/** Id de la fila elegida en la hoja para cada elección. */
export function filaDeEleccion(eleccion: EleccionCuenta): string {
  return eleccion.tipo === 'cuenta'
    ? eleccion.cuenta
    : eleccion.tipo === 'todas'
      ? TODAS_LAS_CUENTAS
      : AUTOMATICA;
}

/** Valor que se guarda al elegir una fila (`null` = «Automática»). */
export function valorDeFila(id: string): string | null {
  return id === AUTOMATICA ? null : id;
}

/** Opciones de la cuenta de una categoría (ficha y «Desde:» del pago fijo). */
function useOpcionesCuenta(categoryId: string, month: string) {
  const { t } = useTranslation();
  const cuentasCat = useCuentasCategorias(month);
  const { elegidas, setEleccion } = useCuentasElegidas();
  const nombreCuenta = (id: string) =>
    cuentasCat.cuentas.find(c => c.id === id)?.name ?? '';
  const eleccion = eleccionDe(elegidas, categoryId, cuentasCat.validas);
  const automatica = cuentasCat.automaticaDe(categoryId);
  const textoAutomatica = automatica
    ? t('Automatic (from your history: {{account}})', {
        account: nombreCuenta(automatica),
      })
    : t('Automatic (no clear account yet)');
  const valor =
    eleccion.tipo === 'cuenta'
      ? nombreCuenta(eleccion.cuenta)
      : eleccion.tipo === 'todas'
        ? t('All accounts')
        : textoAutomatica;
  return {
    cuentasCat,
    textoAutomatica,
    valor,
    elegida: filaDeEleccion(eleccion),
    setEleccion,
  };
}

/**
 * Hoja para elegir la cuenta de una categoría: «Automática (según tu
 * historial: …)», cada cuenta abierta del presupuesto y «Todas las
 * cuentas». La usan «Cuenta» de la ficha y «Desde:» del pago fijo, y las
 * dos escriben el mismo dato (`useCuentasElegidas`).
 */
export function HojaCuentaCategoria({
  categoryId,
  month,
  titulo,
  onCerrar,
  'data-testid': testId = 'hoja-cuenta-categoria',
}: {
  categoryId: string;
  month: string;
  titulo?: string;
  onCerrar: () => void;
  'data-testid'?: string;
}) {
  const { t } = useTranslation();
  const { cuentasCat, textoAutomatica, elegida, setEleccion } =
    useOpcionesCuenta(categoryId, month);
  return (
    <HojaSeleccion
      titulo={titulo ?? t('Which account does this category come from?')}
      textoBuscar={t('Search account')}
      secciones={[
        {
          id: 'cuentas',
          titulo: t('Accounts'),
          sinCabecera: true,
          filas: [
            {
              id: AUTOMATICA,
              nombre: textoAutomatica,
              destacada: elegida === AUTOMATICA,
            },
            ...cuentasCat.cuentas.map(c => ({
              id: c.id,
              nombre: c.name,
              destacada: elegida === c.id,
            })),
            {
              id: TODAS_LAS_CUENTAS,
              nombre: t('All accounts'),
              detalle: t('It can come from any account'),
              destacada: elegida === TODAS_LAS_CUENTAS,
            },
          ],
        },
      ]}
      onElegir={fila => {
        setEleccion(categoryId, valorDeFila(fila.id));
        onCerrar();
      }}
      onCerrar={onCerrar}
      data-testid={testId}
    />
  );
}

/**
 * «Cuenta» de la ficha: de qué cuenta sale el dinero de la categoría (lo
 * usan «Cubrir» y «Mover dinero» para ofrecer las de la misma cuenta).
 * Opciones: «Automática (según tu historial: …)», cada cuenta del
 * presupuesto abierta y «Todas las cuentas». Es el MISMO dato que «Desde:»
 * del pago fijo (`useCuentasElegidas`): cambiar uno cambia el otro.
 */
export function CuentaFicha({
  categoryId,
  month,
}: {
  categoryId: string;
  month: string;
}) {
  const { t } = useTranslation();
  const { cuentasCat, valor } = useOpcionesCuenta(categoryId, month);
  const [abierta, setAbierta] = useState(false);

  // Con una sola cuenta no hay nada que elegir.
  if (cuentasCat.cuentas.length <= 1) {
    return null;
  }

  return (
    <View
      style={{ ...estiloTarjeta('normal', 0), overflow: 'hidden' }}
      data-testid="ficha-cuenta"
    >
      <Button
        variant="bare"
        onPress={() => setAbierta(true)}
        data-testid="ficha-cuenta-elegir"
        aria-label={t('Account: {{account}}', { account: valor })}
        style={{
          minHeight: TACTIL + 8,
          borderRadius: 0,
          justifyContent: 'space-between',
          gap: 12,
          padding: '6px 16px',
          backgroundColor: 'transparent',
          textAlign: 'left',
        }}
      >
        <Text style={{ ...densidad.nombre, color: color.fg }}>
          <Trans>Account</Trans>
        </Text>
        <Text
          style={{
            fontSize: 14,
            color: color.fg2,
            textAlign: 'right',
            minWidth: 0,
          }}
          data-testid="ficha-cuenta-valor"
        >
          {valor}
        </Text>
      </Button>
      {abierta && (
        <HojaCuentaCategoria
          categoryId={categoryId}
          month={month}
          onCerrar={() => setAbierta(false)}
        />
      )}
    </View>
  );
}
